import { useId, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isSuperadminRequired, useServiceAdmin, serviceAdminAction } from "./use-service-admin.ts";
import { SuperadminOnly } from "./SuperadminOnly.tsx";
import { LogsTable } from "./LogsTable.tsx";
import { StatusBadge } from "../../../components/index.ts";
import { formatDateTime, isoAttr } from "../../../lib/datetime.ts";
import { useOptionalAuth } from "../../../auth/index.ts";
import { AgentLifecyclePanel, lifecycleBadge } from "./AgentLifecycle.tsx";
import { AgentPassportPanel } from "./AgentPassport.tsx";
import { SignalsPanel } from "./SignalsPanel.tsx";
import { ControlPolicies, ControlPolicyAssignments } from "./ControlPolicies.tsx";
import { GatewaysPanel, ReceiptChains, ReceiptExplorer, ReceiptKeys } from "./ReceiptAdmin.tsx";
import type { HostObservation } from "./agent-lifecycle-client.ts";

/**
 * The AIS admin console, migrated from the AIS service's `/admin` UI into the control plane:
 *   - Agents: the enrolled agent list (agentId, principal, platform, enrolled, revoked), from
 *     `/admin/api/agents`, with a detail panel.
 *   - Logs: the shared transaction-log viewer.
 *   - Monitor: a recent-activity view. The service's live monitor is an SSE stream; here it is
 *     adapted to a polled recent-logs view through the BFF proxy (the live cadence follows the
 *     shared time-range), which keeps the console on the control plane's one request path.
 */

interface Agent {
  readonly agentId: string;
  readonly principal?: string | {
    readonly email?: string;
    readonly name?: string;
    readonly sub?: string;
    readonly [k: string]: unknown;
  };
  readonly label?: string;
  readonly platform?: string;
  readonly enrolledAt?: string;
  readonly revoked?: boolean;
  readonly ais?: unknown;
  /** The kill-switch fields the AIS adds to the list (G2 #138). */
  readonly agentRef?: string;
  readonly status?: string;
  readonly incidentId?: string | null;
  readonly probationUntil?: string | null;
  readonly lastHostObservation?: HostObservation | null;
  readonly [k: string]: unknown;
}

export function AisConsole(props: { view: "dashboard" | "agents" | "logs" | "monitor" | "signals" | "policies" | "policy-assignments" | "receipts" | "receipt-chains" | "receipt-keys" | "gateways" }): ReactNode {
  if (props.view === "logs") return <LogsTable slug="ais" />;
  if (props.view === "monitor") return <Monitor />;
  if (props.view === "agents") return <Agents />;
  if (props.view === "signals") return <Signals />;
  if (props.view === "policies") return <ControlPolicies />;
  if (props.view === "policy-assignments") return <ControlPolicyAssignments />;
  if (props.view === "receipts") return <ReceiptExplorer />;
  if (props.view === "receipt-chains") return <ReceiptChains />;
  if (props.view === "receipt-keys") return <ReceiptKeys />;
  if (props.view === "gateways") return <GatewaysPanel />;
  return <Dashboard />;
}

/** The Signals tab (G3 #139): administrators get the controls, everyone else reads. */
function Signals(): ReactNode {
  const isAdmin = useOptionalAuth()?.identity?.role === "admin";
  return <SignalsPanel isAdmin={isAdmin} />;
}
/**
 * The AIS dashboard, mirroring the AIS service's `/admin` dashboard and the control plane's IDP
 * dashboard styling: a stat strip, a Server configuration card (static provider facts, the same
 * constants the service renders), and the Credential Format card. The Total Enrolled Agents count is
 * live (read over the same `/admin/api/agents` proxy the Agents tab uses). The Credential Format
 * CURRENT value and its Save are not yet exposed under the proxy's `/admin/api/*` surface, so the
 * control shows the format read-only as "unavailable" rather than implying a value it cannot verify.
 */
function Dashboard(): ReactNode {
  const agentsQuery = useServiceAdmin<readonly Agent[]>("ais", "agents", ["agents"]);
  const agentCount = agentsQuery.data?.length ?? 0;

  const config: ReadonlyArray<readonly [string, string]> = [
    ["AIS URL", "https://ais.oathera.org"],
    ["OIDC Issuer", "https://idp.oathera.org"],
    ["Agent Token Lifetime", "120s"],
  ];

  return (
    <div className="svc-admin svc-admin--full">
      <div className="svc-adm2__stats" aria-label="AIS summary">
        <StatTile label="Port" value="443" />
        <StatTile label="Token Lifetime" value="120s" />
        <StatTile label="Enrolled Agents" value={agentsQuery.isPending ? "…" : String(agentCount)} />
      </div>

      <div className="svc-adm2__grid">
        <section className="svc-adm2__card" aria-label="Server configuration">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Server configuration</h3>
          </div>
          <div className="svc-admin__section">
            <h4 className="svc-admin__section-title">Server Name</h4>
            <p className="svc-admin__stat-value" style={{ fontSize: "16px" }}>AIS (Agent Identity Service)</p>
          </div>
          <dl className="svc-adm2__kv">
            {config.map(([k, v]) => (
              <div key={k} className="svc-admin__kv-row">
                <dt>{k}</dt>
                <dd>{v}</dd>
              </div>
            ))}
            <div className="svc-admin__kv-row">
              <dt>Total Enrolled Agents</dt>
              <dd>{agentsQuery.isPending ? "…" : String(agentCount)}</dd>
            </div>
          </dl>
        </section>

        <CredentialFormatCard />
      </div>
    </div>
  );
}

/** The current credential format as the AIS `/admin/api/credential-format` endpoint returns it. */
interface CredentialFormatResponse {
  readonly format?: "json" | "vc" | string;
}

/**
 * The Credential Format control: reads the current format from
 * `GET /admin/api/credential-format` and changes it with `POST` (the JSON siblings of the AIS
 * service's HTML dashboard control). JSON = inline claims; VC = Verifiable Credentials (VCI).
 * Mirrors the IDP dashboard card styling.
 */
function CredentialFormatCard(): ReactNode {
  const query = useServiceAdmin<CredentialFormatResponse>("ais", "credential-format", ["credential-format"]);
  const queryClient = useQueryClient();
  const current = query.data?.format === "vc" ? "vc" : query.data?.format === "json" ? "json" : null;

  const [choice, setChoice] = useState<"json" | "vc" | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const jsonId = useId();
  const vcId = useId();

  // The selected radio follows the server value until the operator picks one.
  const selected = choice ?? current ?? "json";

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await serviceAdminAction("ais", "credential-format", { format: selected });
      await queryClient.invalidateQueries({ queryKey: ["service-admin", "ais", "credential-format"] });
      setChoice(null);
      setSaved(true);
    } catch (e) {
      setError(
        isSuperadminRequired(e)
          ? "Superadmin only. The credential format applies to every tenant on the AIS, so only a superadmin can change it."
          : e instanceof Error ? e.message : "The credential format could not be saved.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="svc-adm2__card" aria-label="Credential format">
      <div className="svc-adm2__card-head">
        <h3 className="svc-admin__h">Credential Format</h3>
      </div>
      {query.isError ? (
        <div className="state-box" data-kind="error" role="alert">
          <strong>Could not load the credential format</strong>
          <span>The AIS admin service could not be reached. {query.error.message}</span>
        </div>
      ) : (
        <>
          <div className="svc-admin__field" role="radiogroup" aria-label="Credential format">
            <label className="svc-adm2__radio" htmlFor={jsonId}>
              <input id={jsonId} type="radio" name="credential-format" value="json" checked={selected === "json"} disabled={query.isPending || saving} onChange={() => { setChoice("json"); setSaved(false); }} />
              JSON (inline claims)
            </label>
            <label className="svc-adm2__radio" htmlFor={vcId}>
              <input id={vcId} type="radio" name="credential-format" value="vc" checked={selected === "vc"} disabled={query.isPending || saving} onChange={() => { setChoice("vc"); setSaved(false); }} />
              Verifiable Credentials (VCI)
            </label>
          </div>
          {error !== null && <p className="svc-admin__err" role="alert">{error}</p>}
          <div className="svc-admin__actions">
            <button type="button" className="svc-admin__btn" onClick={() => void save()} disabled={query.isPending || saving || current === null || selected === current}>
              {saving ? "Saving…" : "Save"}
            </button>
            {saved && <span className="svc-adm2__card-sub" role="status">Saved.</span>}
          </div>
          <p className="svc-admin__muted" style={{ fontSize: "12px" }}>
            {query.isPending
              ? "Loading the current format…"
              : <>Current: <strong>{current ?? "unknown"}</strong>. Changes take effect on the next token issuance.</>}
          </p>
        </>
      )}
    </section>
  );
}

type AgentSortKey = "label" | "agentId" | "principal" | "platform" | "enrolledAt" | "status";
type SortDirection = "ascending" | "descending";

function agentPrincipalText(agent: Agent): string {
  if (typeof agent.principal === "string") return agent.principal;
  return agent.principal?.email ?? agent.principal?.name ?? agent.principal?.sub ?? "";
}

function agentStatusText(agent: Agent, nowMs: number): string {
  return lifecycleBadge(agent, nowMs).label;
}

function compareAgentValues(left: string | number, right: string | number): number {
  if (typeof left === "number" && typeof right === "number") return left - right;
  return String(left).localeCompare(String(right), undefined, { sensitivity: "base", numeric: true });
}

function Agents(): ReactNode {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<AgentSortKey>("enrolledAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("descending");
  const query = useServiceAdmin<readonly Agent[]>("ais", "agents", ["agents"]);
  const rows = query.data ?? [];
  // The server enforces every action; the role only decides whether the admin-only buttons are shown.
  const isAdmin = useOptionalAuth()?.identity?.role === "admin";
  // Look the selection up in the latest list so a suspend / reinstate shows its new state at once.
  const selected = rows.find((a) => a.agentId === selectedId) ?? null;
  const nowMs = Date.now();
  const sortedRows = useMemo(() => {
    const value = (agent: Agent): string | number => {
      switch (sortKey) {
        case "agentId": return agent.agentId;
        case "principal": return agentPrincipalText(agent);
        case "platform": return agent.platform ?? "";
        case "enrolledAt": {
          const parsed = Date.parse(agent.enrolledAt ?? "");
          return Number.isFinite(parsed) ? parsed : 0;
        }
        case "status": return agentStatusText(agent, nowMs);
        case "label":
        default: return agent.label ?? agent.agentRef ?? agentPrincipalText(agent);
      }
    };
    const direction = sortDirection === "ascending" ? 1 : -1;
    return [...rows].sort((left, right) => {
      const compared = compareAgentValues(value(left), value(right));
      if (compared !== 0) return compared * direction;
      return left.agentId.localeCompare(right.agentId);
    });
  }, [rows, sortDirection, sortKey, nowMs]);

  if (query.isError) {
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load agents</strong>
        <span>The AIS admin service could not be reached. {query.error.message}</span>
      </div>
    );
  }

  const total = rows.length;
  const revokedCount = rows.filter((a) => a.revoked === true).length;
  const suspendedCount = rows.filter((a) => a.revoked !== true && a.status === "suspended").length;
  const activeCount = total - revokedCount - suspendedCount;

  return (
    <div className="svc-admin svc-admin--full">
      <div className="svc-adm2__stats" aria-label="Agent summary">
        <StatTile label="Agents" value={query.isPending ? "…" : String(total)} />
        <StatTile label="Active" value={query.isPending ? "…" : String(activeCount)} />
        <StatTile label="Suspended" value={query.isPending ? "…" : String(suspendedCount)} />
        <StatTile label="Revoked" value={query.isPending ? "…" : String(revokedCount)} />
      </div>

      <div className="svc-admin__split">
        <section className="svc-adm2__card" aria-label="Enrolled agents">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Agents</h3>
            <div className="svc-admin__sort-controls" aria-label="Sort agents">
              <label>
                <span className="sr-only">Sort agents by</span>
                <select
                  className="control__select"
                  value={sortKey}
                  onChange={(event) => setSortKey(event.target.value as AgentSortKey)}
                >
                  <option value="enrolledAt">Enrolled date</option>
                  <option value="label">Label</option>
                  <option value="agentId">Agent ID</option>
                  <option value="principal">Principal</option>
                  <option value="platform">Platform</option>
                  <option value="status">Status</option>
                </select>
              </label>
              <button
                type="button"
                className="svc-admin__btn"
                aria-label={`Sort ${sortDirection}`}
                title={`Currently ${sortDirection}; click to reverse`}
                onClick={() => setSortDirection((current) => current === "ascending" ? "descending" : "ascending")}
              >
                {sortDirection === "ascending" ? "Ascending ↑" : "Descending ↓"}
              </button>
            </div>
            {!query.isPending && total > 0 && <span className="svc-adm2__card-sub">{total} enrolled in this team</span>}
          </div>
          {query.isPending ? (
            <div className="state-box" data-kind="loading" role="status"><strong>Loading…</strong><span>Fetching agents.</span></div>
          ) : rows.length === 0 ? (
            <div className="state-box" data-kind="empty"><strong>No agents</strong><span>No agents have been enrolled yet.</span></div>
          ) : (
            <ul className="svc-adm2__rows">
              {sortedRows.map((a) => {
                const badge = lifecycleBadge(a, nowMs);
                return (
                  <li key={a.agentId}>
                    <button type="button" className="svc-admin__row" data-active={selected?.agentId === a.agentId ? "true" : undefined} onClick={() => setSelectedId(a.agentId)}>
                      <StatusBadge level={badge.level} label={badge.label} size="sm" />
                      <span className="svc-admin__row-subject">
                        {(a.label ?? a.agentRef ?? agentPrincipalText(a)) || "Agent"}
                        {agentPrincipalText(a) && <small>{agentPrincipalText(a)}</small>}
                      </span>
                      {a.platform !== undefined && <span className="svc-admin__row-type">{a.platform}</span>}
                      {a.enrolledAt !== undefined && (
                        <time className="svc-admin__row-time" dateTime={isoAttr(Date.parse(a.enrolledAt))}>{safeDate(a.enrolledAt)}</time>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
        <section className="svc-adm2__card" aria-label="Agent detail">
          {selected === null ? (
            <div className="state-box" data-kind="empty"><strong>Select an agent</strong><span>Choose an agent to inspect its full record.</span></div>
          ) : (
            <div className="svc-admin__cred">
              <div className="svc-adm2__card-head">
                <h3 className="svc-admin__h">{(selected.label ?? agentPrincipalText(selected)) || "Agent"}</h3>
                <StatusBadge level={lifecycleBadge(selected, nowMs).level} label={lifecycleBadge(selected, nowMs).label} size="sm" />
              </div>
              <AgentLifecyclePanel key={selected.agentId} agent={selected} isAdmin={isAdmin} />
              <AgentPassportPanel key={`passport-${selected.agentId}`} agentRef={selected.agentRef} />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * One AIS transaction-log entry, as `/admin/api/logs` returns it. The Monitor derives its view from
 * these — the SAME rows the Logs tab shows — filtered to the tracked agent-lifecycle event types.
 */
interface AisLogEntry {
  readonly id: number;
  readonly timestamp: string;
  readonly event?: { readonly type?: string | null; readonly metadata?: Record<string, unknown> | null } | null;
}

/**
 * The agent-lifecycle event types the Live Agent Monitor tracks (mirrors the AIS service's
 * TRACKED_EVENTS). Order drives the Event Breakdown panel; the accent is a theme token (never a
 * hard-coded hex) so the feed reads correctly in light and dark.
 */
const MONITOR_EVENT_TYPES = [
  { type: "enrollment_start", label: "enrollment start", accent: "var(--status-healthy)" },
  { type: "spiffe_enrollment", label: "spiffe enrollment", accent: "var(--status-warn)" },
  { type: "token_issuance", label: "token issuance", accent: "var(--accent)" },
  { type: "federation_token_issuance", label: "federation token issuance", accent: "color-mix(in srgb, var(--accent) 60%, var(--status-warn))" },
  { type: "agent_verification", label: "agent verification", accent: "color-mix(in srgb, var(--accent) 55%, var(--status-error))" },
  { type: "verification_failure", label: "verification failure", accent: "var(--status-error)" },
] as const;

const MONITOR_TYPE_SET = new Set<string>(MONITOR_EVENT_TYPES.map((t) => t.type));

function metaString(v: unknown): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

/**
 * The AIS "Live Agent Monitor" (migrated from the service's SSE dashboard). The control plane reads
 * on its single request path — the shared `/admin/api/logs` proxy read, polled on the time-range
 * cadence — rather than a raw SSE stream, and derives the same surfaces the service rendered:
 *   - six stat tiles (Total events, Enrollments, Tokens issued, Verifications, Errors, Unique agents),
 *   - a Live Detection Feed (newest first, colour-accented per event type), and
 *   - an Event Breakdown by type.
 * Everything is theme-tokened (light/dark). "Live" follows the shared time-range (pause freezes it).
 */
function Monitor(): ReactNode {
  const query = useServiceAdmin<readonly AisLogEntry[]>("ais", "logs?limit=200", ["logs", "monitor"]);

  const { events, stats, breakdown } = useMemo(() => {
    const rows = query.data ?? [];
    const evts = rows
      .filter((r) => typeof r.event?.type === "string" && MONITOR_TYPE_SET.has(r.event.type as string))
      .map((r) => {
        const md = (r.event?.metadata ?? {}) as Record<string, unknown>;
        const agentId = metaString(md["agent_id"] ?? md["key_thumbprint"]) || null;
        return {
          id: r.id,
          type: r.event?.type as string,
          agentId,
          principalEmail: metaString(md["principal_email"]) || null,
          timestamp: r.timestamp,
          metadata: md,
        };
      })
      // Newest first for the feed.
      .sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp));

    const byType = new Map<string, number>();
    const agents = new Set<string>();
    for (const e of evts) {
      byType.set(e.type, (byType.get(e.type) ?? 0) + 1);
      if (e.agentId) agents.add(e.agentId);
    }
    const n = (t: string) => byType.get(t) ?? 0;
    return {
      events: evts,
      breakdown: MONITOR_EVENT_TYPES.map((t) => ({ ...t, count: n(t.type) })),
      stats: {
        total: evts.length,
        enrollments: n("enrollment_start") + n("spiffe_enrollment"),
        tokens: n("token_issuance") + n("federation_token_issuance"),
        verifications: n("agent_verification"),
        errors: n("verification_failure"),
        uniqueAgents: agents.size,
      },
    };
  }, [query.data]);

  if (query.isError) {
    if (isSuperadminRequired(query.error)) return <SuperadminOnly what="The AIS monitor (built from the AIS request log)" />;
    return (
      <div className="state-box" data-kind="error" role="alert">
        <strong>Could not load the monitor</strong>
        <span>The AIS admin service could not be reached. {query.error.message}</span>
      </div>
    );
  }

  const accentFor = (type: string): string =>
    MONITOR_EVENT_TYPES.find((t) => t.type === type)?.accent ?? "var(--border)";
  const labelFor = (type: string): string =>
    MONITOR_EVENT_TYPES.find((t) => t.type === type)?.label ?? type.replace(/_/g, " ");

  return (
    <div className="svc-admin svc-admin--full">
      <div className="svc-mon__status" aria-live="polite">
        <span className="svc-mon__dot" data-live={!query.isPending ? "true" : undefined} aria-hidden="true" />
        <span>{query.isPending ? "Connecting…" : "Live — polling recent events"}</span>
      </div>

      <div className="svc-adm2__stats" aria-label="Monitor summary">
        <StatTile label="Total events" value={query.isPending ? "…" : String(stats.total)} />
        <StatTile label="Enrollments" value={query.isPending ? "…" : String(stats.enrollments)} />
        <StatTile label="Tokens issued" value={query.isPending ? "…" : String(stats.tokens)} />
        <StatTile label="Verifications" value={query.isPending ? "…" : String(stats.verifications)} />
        <StatTile label="Errors" value={query.isPending ? "…" : String(stats.errors)} />
        <StatTile label="Unique agents" value={query.isPending ? "…" : String(stats.uniqueAgents)} />
      </div>

      <div className="svc-mon__grid">
        <section className="svc-adm2__card" aria-label="Live detection feed">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Live Detection Feed</h3>
            {!query.isPending && <span className="svc-adm2__card-sub">{events.length} events</span>}
          </div>
          {query.isPending ? (
            <div className="state-box" data-kind="loading" role="status"><strong>Loading…</strong><span>Fetching recent events.</span></div>
          ) : events.length === 0 ? (
            <div className="state-box" data-kind="empty"><strong>No events</strong><span>No agent-lifecycle events have been recorded in this window.</span></div>
          ) : (
            <ul className="svc-mon__feed">
              {events.map((e) => {
                const extra = Object.entries(e.metadata)
                  .filter(([k]) => k !== "agent_id" && k !== "principal_email" && k !== "key_thumbprint")
                  .map(([k, v]) => `${k}: ${metaString(v)}`);
                const meta = [
                  e.agentId ? `Agent: ${e.agentId}` : null,
                  e.principalEmail ? `Principal: ${e.principalEmail}` : null,
                  ...extra,
                ].filter((x): x is string => x !== null);
                return (
                  <li key={e.id} className="svc-mon__event" style={{ borderLeftColor: accentFor(e.type) }}>
                    <span className="svc-mon__event-type" style={{ color: accentFor(e.type) }}>{labelFor(e.type)}</span>
                    {meta.length > 0 && <span className="svc-mon__event-meta">{meta.join(" · ")}</span>}
                    <time className="svc-mon__event-ts" dateTime={isoAttr(Date.parse(e.timestamp))}>{safeDate(e.timestamp)}</time>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section className="svc-adm2__card" aria-label="Event breakdown">
          <div className="svc-adm2__card-head">
            <h3 className="svc-admin__h">Event Breakdown</h3>
          </div>
          <dl className="svc-mon__breakdown">
            {breakdown.map((b) => (
              <div key={b.type} className="svc-mon__breakdown-row">
                <dt><span className="svc-mon__swatch" style={{ background: b.accent }} aria-hidden="true" />{b.label}</dt>
                <dd>{query.isPending ? "…" : b.count}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>
    </div>
  );
}

/** A compact dashboard stat tile — matches the IDP/VCI console tiles. */
function StatTile(props: { label: string; value: string }): ReactNode {
  return (
    <div className="svc-admin__stat">
      <span className="svc-admin__stat-value">{props.value}</span>
      <span className="svc-admin__stat-label">{props.label}</span>
    </div>
  );
}

function safeDate(iso: string): string {
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? formatDateTime(ms) : iso;
}
